/**
 * InlineFilterBar Component
 * Filters that stay visible on screen and apply live — replacing the
 * "click a button to open a popover, then Apply" pattern.
 *
 * Layout:
 *   [ search ] [ inline controls… ] [ overflow ] · · · [ actions ]
 *   [ N resultados ] | [ active chips ] [ Limpar tudo ]
 *
 * The search is debounced internally, so screens don't repeat that wiring.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import type { ActiveChip } from '../../hooks/useFilterState';
import { FILTER_CELL_CLASS, FILTER_CELL_WIDE_CLASS, InlineToggles } from './InlineToggles';
import type { InlineTogglesProps } from './InlineToggles';
import { pickColumns } from './filterBarColumns';

const FIELD_GAP_PX = 12;
/** Below this the columns get too narrow to read, so fields go one per row. */
const FIELD_MIN_WIDTH_PX = 200;

const isToggles = (child: React.ReactNode): child is React.ReactElement<InlineTogglesProps> =>
  React.isValidElement<InlineTogglesProps>(child) && child.type === InlineToggles;

/** A chip row sizes itself, so it must not take a column away from the fields. */
const takesOwnWidth = (child: React.ReactNode): boolean =>
  isToggles(child) && child.props.items.length > 1;

/** Yes/no filters raise no chip, so only their own state says they are on. */
const hasToggleOn = (child: React.ReactNode): boolean =>
  isToggles(child) && child.props.items.some((item) => item.checked);

export interface InlineFilterBarProps {
  /** Text search placeholder. Omit `onSearchChange` to hide the search box entirely. */
  searchPlaceholder?: string;
  /** Current committed search value (source of truth lives in the parent). */
  searchValue?: string;
  /** Called with the new search text, already debounced. Omit to render no search box. */
  onSearchChange?: (value: string) => void;
  /** Inline filter controls, always visible (e.g. InlineMultiSelect). */
  children?: React.ReactNode;
  /** Optional overflow slot for rare filters (e.g. a FilterPopover). */
  overflow?: React.ReactNode;
  /** Right-aligned action buttons (e.g. Export). */
  actions?: React.ReactNode;
  /** Active filter chips (from useFilterState). */
  chips?: ActiveChip[];
  onClearAll?: () => void;
  /** Total result count for the current filters. */
  resultCount?: number;
  /** Noun for the result count, e.g. "empresas". */
  resultNoun?: string;
  loading?: boolean;
  searchDebounceMs?: number;
}

export const InlineFilterBar: React.FC<InlineFilterBarProps> = ({
  searchPlaceholder,
  searchValue = '',
  onSearchChange,
  children,
  overflow,
  actions,
  chips = [],
  onClearAll,
  resultCount,
  resultNoun = 'resultados',
  loading = false,
  searchDebounceMs = 300,
}) => {
  const [text, setText] = useState(searchValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmitted = useRef(searchValue);

  // Keep the input in sync when the committed value changes externally
  // (e.g. "Limpar tudo" resets it) without interrupting active typing.
  useEffect(() => {
    if (searchValue !== lastEmitted.current) {
      setText(searchValue);
      lastEmitted.current = searchValue;
    }
  }, [searchValue]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleTextChange = (value: string) => {
    setText(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastEmitted.current = value;
      onSearchChange?.(value);
    }, searchDebounceMs);
  };

  const childList = React.Children.toArray(children);
  const columns = pickColumns(
    childList.filter((child) => !takesOwnWidth(child)).length + (onSearchChange ? 1 : 0),
  );

  const hasChips = chips.length > 0;
  const hasCount = typeof resultCount === 'number';
  const canClearAll = hasChips || childList.some(hasToggleOn);

  return (
    <Box>
      <Box
        display="flex"
        gap={`${FIELD_GAP_PX}px`}
        alignItems="center"
        flexWrap="wrap"
        sx={(theme) => ({
          // Fields share the bar in equal columns and wrap instead of shrinking —
          // screens with many filters used to squeeze them all onto one row. The
          // repeated class outranks the width each screen passes in.
          [`& > .MuiFormControl-root.MuiFormControl-root,
            & > .MuiAutocomplete-root.MuiAutocomplete-root,
            & > .${FILTER_CELL_CLASS}.${FILTER_CELL_CLASS}`]: {
            flex: `0 1 calc((100% - ${(columns - 1) * FIELD_GAP_PX}px) / ${columns})`,
            minWidth: FIELD_MIN_WIDTH_PX,
            maxWidth: 'none',
            // Narrow enough that a column falls under the pixel floor: one field
            // per row, filling it, instead of a short field and dead space.
            [theme.breakpoints.down('md')]: {
              flexBasis: '100%',
            },
          },
          // A row of chips is only readable side by side, so this cell takes the
          // width its chips need — up to the whole row — instead of one column.
          [`& > .${FILTER_CELL_WIDE_CLASS}.${FILTER_CELL_WIDE_CLASS}`]: {
            flex: '0 1 auto',
            minWidth: 'auto',
            maxWidth: '100%',
          },
        })}
      >
        {onSearchChange && (
          <TextField
            placeholder={searchPlaceholder}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            size="small"
            inputProps={{ 'aria-label': searchPlaceholder ?? 'Buscar' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: text ? (
                <InputAdornment position="end">
                  <ClearIcon
                    color="action"
                    fontSize="small"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleTextChange('')}
                    aria-label="Limpar busca"
                    role="button"
                  />
                </InputAdornment>
              ) : undefined,
            }}
          />
        )}

        {children}

        {overflow}

        {actions && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            {actions}
          </>
        )}
      </Box>

      {(hasCount || canClearAll) && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5, minHeight: 32 }}
        >
          {hasCount && (
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {resultCount}
              </Box>{' '}
              {resultNoun}
            </Typography>
          )}

          {hasCount && hasChips && <Divider orientation="vertical" flexItem />}

          {chips.map((chip) => (
            <Chip
              key={chip.key}
              label={`${chip.label}: ${chip.value}`}
              onDelete={chip.onDelete}
              size="small"
              color="primary"
              variant="outlined"
              disabled={loading}
            />
          ))}

          {canClearAll && onClearAll && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={onClearAll}
              disabled={loading}
            >
              Limpar tudo
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};
